"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo } from "react";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import type { TraitsData } from "../types/TraitsData";
import MemesArtSubmissionFile from "@/components/waves/memes/MemesArtSubmissionFile";
import ArtworkDetails from "../details/ArtworkDetails";
import MemesArtSubmissionTraits from "@/components/waves/memes/MemesArtSubmissionTraits";
import type { SubmissionPhase } from "../ui/SubmissionProgress";
import SubmissionProgress from "../ui/SubmissionProgress";
import { useTraitsValidation } from "../validation";
import type { InteractiveMediaMimeType, InteractiveMediaProvider } from "../constants/media";

/**
 * Required fields for submission
 * All fields are required for submission
 */

/**
 * Fields that are read-only and should be skipped in validation
 * These are pre-populated by the system
 */
const READ_ONLY_FIELDS = ["seizeArtistProfile"] as const;

/**
 * Get tooltip text for submit button based on current state
 * Helps users understand why the button might be disabled
 */
function getSubmitButtonTooltip(isDisabled: boolean, isFormComplete: boolean, artworkUploaded: boolean, traits: TraitsData): string {
  if (!isDisabled) return "";
  if (!artworkUploaded) {
    return "Please upload artwork";
  }
  if (!isFormComplete) {
    // Check each field and return specific message for the first empty one
    // This helps the user understand what's missing
    for (const field of Object.keys(traits) as Array<keyof TraitsData>) {
      // Skip read-only fields
      if (READ_ONLY_FIELDS.includes(field as any)) continue;
      const value = traits[field];
      const fieldName = field.replace(/([A-Z])/g, " $1").toLowerCase();

      // String fields (including dropdowns)
      if (typeof value === "string") {
        if (!value.trim()) {
          if (field === "memeName" || field === "palette") {
            return `Please select a ${fieldName}`;
          } else {
            return `Please enter ${fieldName}`;
          }
        }
      }
      // Number fields
      else if (typeof value === "number") {
        if (value === null || value === undefined || isNaN(value)) {
          return `Please enter ${fieldName}`;
        }
      }
      // Boolean fields need explicit value
      else if (typeof value === "boolean") {
        if (value === null || value === undefined) {
          return `Please set ${fieldName}`;
        }
      }
      // Handle potential null/undefined
      else if (value === null || value === undefined) {
        return `Please complete ${fieldName}`;
      }
    }
    return "All fields are required";
  }
  return "Please fix validation errors before submitting";
}

/**
 * Checks if form meets basic submission requirements
 * ALL fields are required except read-only ones
 */
function checkFormCompleteness(traits: TraitsData, artworkUploaded: boolean): boolean {
  // Must have artwork
  if (!artworkUploaded) return false;

  // Check every field in TraitsData
  for (const field of Object.keys(traits) as Array<keyof TraitsData>) {
    // Skip read-only fields that are pre-populated
    if (READ_ONLY_FIELDS.includes(field as any)) continue;
    const value = traits[field];

    // Handle string fields - must not be empty
    if (typeof value === "string") {
      if (!value.trim()) return false;
    }
    // Handle number fields - must be a defined number and not zero
    else if (typeof value === "number") {
      if (value === null || value === undefined || isNaN(value) || value === 0) return false;
    }
    // Handle boolean fields - must be explicitly true or false
    else if (typeof value === "boolean") {
      if (value === null || value === undefined) return false;
    }
    // Handle potential null/undefined values
    else if (value === null || value === undefined) {
      return false;
    }
  }
  return true;
}
interface ArtworkStepProps {
  readonly traits: TraitsData;
  readonly artworkUploaded: boolean;
  readonly artworkUrl: string;
  readonly setArtworkUploaded: (uploaded: boolean) => void;
  readonly handleFileSelect: (file: File) => void;
  readonly mediaSource: "upload" | "url";
  readonly setMediaSource: (mode: "upload" | "url") => void;
  readonly externalHash: string;
  readonly externalProvider: InteractiveMediaProvider;
  readonly externalConstructedUrl: string;
  readonly externalPreviewUrl: string;
  readonly externalMimeType: InteractiveMediaMimeType;
  readonly externalError: string | null;
  readonly externalValidationStatus: "idle" | "pending" | "valid" | "invalid";
  readonly isExternalMediaValid: boolean;
  readonly onExternalHashChange: (value: string) => void;
  readonly onExternalProviderChange: (value: InteractiveMediaProvider) => void;
  readonly onExternalMimeTypeChange: (value: InteractiveMediaMimeType) => void;
  readonly onClearExternalMedia: () => void;
  readonly onSubmit: () => void;
  readonly onCancel?: (() => void) | undefined; // Added cancel handler prop
  readonly updateTraitField: <K extends keyof TraitsData>(field: K, value: TraitsData[K]) => void;
  readonly setTraits: (traits: Partial<TraitsData>) => void;
  readonly isSubmitting?: boolean | undefined;
  readonly submissionPhase?: SubmissionPhase | undefined;
  readonly initialTraits?: TraitsData | undefined;
  // Additional props for SubmissionProgress
  readonly uploadProgress?: number | undefined;
  readonly fileInfo?: {
    name: string;
    size: number;
  } | null | undefined;
  readonly submissionError?: string | undefined;
}

/**
 * ArtworkStep - Component for the artwork submission step
 *
 * This component directly includes all the needed components for
 * the artwork submission process in a clear, sequential layout.
 * The submit button is fixed at the bottom of the page and changes
 * appearance based on the current submission phase.
 */
const ArtworkStep: React.FC<ArtworkStepProps> = t0 => {
  const $ = _c(89);
  const {
    traits,
    artworkUploaded,
    artworkUrl,
    setArtworkUploaded,
    handleFileSelect,
    mediaSource,
    setMediaSource,
    externalHash,
    externalProvider,
    externalConstructedUrl,
    externalPreviewUrl,
    externalMimeType,
    externalError,
    externalValidationStatus,
    isExternalMediaValid,
    onExternalHashChange,
    onExternalProviderChange,
    onExternalMimeTypeChange,
    onClearExternalMedia,
    onSubmit,
    onCancel,
    updateTraitField,
    setTraits,
    isSubmitting: t1,
    submissionPhase: t2,
    initialTraits,
    uploadProgress: t3,
    fileInfo: t4,
    submissionError
  } = t0;
  const isSubmitting = t1 === undefined ? false : t1;
  const submissionPhase = t2 === undefined ? "idle" : t2;
  const uploadProgress = t3 === undefined ? 0 : t3;
  const fileInfo = t4 === undefined ? null : t4;
  const validation = useTraitsValidation(traits, initialTraits || traits);
  let t5;
  if ($[0] !== artworkUploaded || $[1] !== traits) {
    t5 = checkFormCompleteness(traits, artworkUploaded);
    $[0] = artworkUploaded;
    $[1] = traits;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const isFormComplete = t5;
  let t6;
  if ($[3] !== updateTraitField) {
    t6 = title => {
      updateTraitField("title", title);
    };
    $[3] = updateTraitField;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleTitleChange = t6;
  let t7;
  if ($[5] !== updateTraitField) {
    t7 = description => {
      updateTraitField("description", description);
    };
    $[5] = updateTraitField;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const handleDescriptionChange = t7;
  let t8;
  if ($[7] !== validation) {
    t8 = field => {
      validation.markFieldTouched(field);
    };
    $[7] = validation;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleFieldBlur = t8;
  let t9;
  if ($[9] !== onSubmit || $[10] !== validation) {
    t9 = () => {
      const validationResult = validation.validateAll();
      if (validationResult.isValid) {
        onSubmit();
      } else {
        validation.focusFirstInvalidField();
      }
    };
    $[9] = onSubmit;
    $[10] = validation;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleSubmit = t9;
  let t10;
  bb0: {
    if (!isFormComplete) {
      t10 = true;
      break bb0;
    }
    if (isSubmitting) {
      t10 = true;
      break bb0;
    }
    if (submissionPhase === "success") {
      t10 = true;
      break bb0;
    }
    if (validation.submitAttempted && !validation.isValid) {
      t10 = true;
      break bb0;
    }
    t10 = false;
  }
  const isSubmitDisabled = t10;
  let t11;
  if ($[12] !== submissionPhase) {
    t11 = () => {
      switch (submissionPhase) {
        case "uploading":
          {
            return "Uploading...";
          }
        case "processing":
          {
            return "Processing...";
          }
        case "success":
          {
            return "\u2713 Submission Complete";
          }
        case "error":
          {
            return "Try Again";
          }
        default:
          {
            return "Continue";
          }
      }
    };
    $[12] = submissionPhase;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const getButtonText = t11;
  let t12;
  if ($[14] !== submissionPhase) {
    t12 = () => {
      switch (submissionPhase) {
        case "success":
          {
            return "tw-bg-green-600 hover:tw-bg-green-700";
          }
        case "error":
          {
            return "tw-bg-red-600 hover:tw-bg-red-700";
          }
        default:
          {
            return "";
          }
      }
    };
    $[14] = submissionPhase;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const getButtonClass = t12;
  let t13;
  if ($[16] !== artworkUploaded || $[17] !== artworkUrl || $[18] !== externalConstructedUrl || $[19] !== externalError || $[20] !== externalHash || $[21] !== externalMimeType || $[22] !== externalPreviewUrl || $[23] !== externalProvider || $[24] !== externalValidationStatus || $[25] !== handleFileSelect || $[26] !== isExternalMediaValid || $[27] !== mediaSource || $[28] !== onClearExternalMedia || $[29] !== onExternalHashChange || $[30] !== onExternalMimeTypeChange || $[31] !== onExternalProviderChange || $[32] !== setArtworkUploaded || $[33] !== setMediaSource) {
    t13 = <div className="tw-px-4 md:tw-px-8 lg:tw-pr-0 tw-w-full lg:tw-w-1/2"><div className="tw-sticky tw-top-0 tw-h-[calc(100vh-14rem)]"><MemesArtSubmissionFile artworkUploaded={artworkUploaded} artworkUrl={artworkUrl} setArtworkUploaded={setArtworkUploaded} handleFileSelect={handleFileSelect} mediaSource={mediaSource} setMediaSource={setMediaSource} externalHash={externalHash} externalProvider={externalProvider} externalConstructedUrl={externalConstructedUrl} externalPreviewUrl={externalPreviewUrl} externalMimeType={externalMimeType} externalError={externalError} externalValidationStatus={externalValidationStatus} isExternalMediaValid={isExternalMediaValid} onExternalHashChange={onExternalHashChange} onExternalProviderChange={onExternalProviderChange} onExternalMimeTypeChange={onExternalMimeTypeChange} onClearExternalMedia={onClearExternalMedia} /></div></div>;
    $[16] = artworkUploaded;
    $[17] = artworkUrl;
    $[18] = externalConstructedUrl;
    $[19] = externalError;
    $[20] = externalHash;
    $[21] = externalMimeType;
    $[22] = externalPreviewUrl;
    $[23] = externalProvider;
    $[24] = externalValidationStatus;
    $[25] = handleFileSelect;
    $[26] = isExternalMediaValid;
    $[27] = mediaSource;
    $[28] = onClearExternalMedia;
    $[29] = onExternalHashChange;
    $[30] = onExternalMimeTypeChange;
    $[31] = onExternalProviderChange;
    $[32] = setArtworkUploaded;
    $[33] = setMediaSource;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  let t15;
  if ($[35] !== handleFieldBlur) {
    t14 = () => handleFieldBlur("title");
    t15 = () => handleFieldBlur("description");
    $[35] = handleFieldBlur;
    $[36] = t14;
    $[37] = t15;
  } else {
    t14 = $[36];
    t15 = $[37];
  }
  let t16;
  if ($[38] !== handleDescriptionChange || $[39] !== handleTitleChange || $[40] !== t14 || $[41] !== t15 || $[42] !== traits.description || $[43] !== traits.title || $[44] !== validation.errors.description || $[45] !== validation.errors.title) {
    t16 = <ArtworkDetails title={traits.title} description={traits.description} onTitleChange={handleTitleChange} onDescriptionChange={handleDescriptionChange} titleError={validation.errors.title} descriptionError={validation.errors.description} onTitleBlur={t14} onDescriptionBlur={t15} />;
    $[38] = handleDescriptionChange;
    $[39] = handleTitleChange;
    $[40] = t14;
    $[41] = t15;
    $[42] = traits.description;
    $[43] = traits.title;
    $[44] = validation.errors.description;
    $[45] = validation.errors.title;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== handleFieldBlur || $[48] !== setTraits || $[49] !== traits || $[50] !== validation.errors) {
    t17 = <MemesArtSubmissionTraits traits={traits} setTraits={setTraits} validationErrors={validation.errors} onFieldBlur={handleFieldBlur} />;
    $[47] = handleFieldBlur;
    $[48] = setTraits;
    $[49] = traits;
    $[50] = validation.errors;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t16 || $[53] !== t17) {
    t18 = <div className="tw-px-4 md:tw-px-8 lg:tw-pl-0 tw-full lg:tw-w-1/2 tw-flex tw-flex-col tw-gap-y-6 tw-pb-6">{t16}{t17}</div>;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t13 || $[56] !== t18) {
    t19 = <div className="tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-relative"><div className="tw-relative tw-flex tw-gap-x-8 tw-gap-y-6 tw-flex-col lg:tw-flex-row tw-w-full">{t13}{t18}</div></div>;
    $[55] = t13;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== fileInfo || $[59] !== submissionError || $[60] !== submissionPhase || $[61] !== uploadProgress) {
    t20 = submissionPhase !== "idle" && <div className="tw-mb-4"><SubmissionProgress phase={submissionPhase} progress={uploadProgress} fileInfo={fileInfo} error={submissionError} /></div>;
    $[58] = fileInfo;
    $[59] = submissionError;
    $[60] = submissionPhase;
    $[61] = uploadProgress;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== onCancel || $[64] !== submissionPhase) {
    t21 = onCancel && submissionPhase !== "success" ? <button onClick={submissionPhase === "uploading" || submissionPhase === "processing" ? undefined : onCancel} disabled={submissionPhase === "uploading" || submissionPhase === "processing"} className="tw-border tw-border-solid tw-border-iron-800 tw-ring-1 tw-ring-iron-700 desktop-hover:hover:tw-ring-iron-650 tw-rounded-lg tw-bg-iron-800 tw-px-3.5 tw-py-3 tw-text-sm tw-font-semibold tw-text-iron-300 tw-shadow-sm desktop-hover:hover:tw-bg-iron-700 desktop-hover:hover:tw-border-iron-700 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-iron-700 tw-transition tw-duration-300 tw-ease-out disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" type="button">Cancel</button> : <div />;
    $[63] = onCancel;
    $[64] = submissionPhase;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const t22 = `tw-transition-all tw-duration-300 ${getButtonClass()}`;
  const t23 = isSubmitting && submissionPhase !== "success" && submissionPhase !== "error";
  let t24;
  if ($[66] !== artworkUploaded || $[67] !== isFormComplete || $[68] !== isSubmitDisabled || $[69] !== traits) {
    t24 = getSubmitButtonTooltip(isSubmitDisabled, isFormComplete, artworkUploaded, traits);
    $[66] = artworkUploaded;
    $[67] = isFormComplete;
    $[68] = isSubmitDisabled;
    $[69] = traits;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  const t25 = getButtonText();
  let t26;
  if ($[71] !== handleSubmit || $[72] !== isSubmitDisabled || $[73] !== t23 || $[74] !== t24 || $[75] !== t25) {
    t26 = <PrimaryButton padding="tw-py-3 tw-px-3.5" onClicked={handleSubmit} loading={t23} disabled={isSubmitDisabled} title={t24}>{t25}</PrimaryButton>;
    $[71] = handleSubmit;
    $[72] = isSubmitDisabled;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== t22 || $[78] !== t26) {
    t27 = <div className={t22}>{t26}</div>;
    $[77] = t22;
    $[78] = t26;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== t21 || $[81] !== t27) {
    t28 = <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-3">{t21}{t27}</div>;
    $[80] = t21;
    $[81] = t27;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== t20 || $[84] !== t28) {
    t29 = <div className="tw-border-t tw-border-iron-800 tw-border-solid tw-border-b-0 tw-border-x-0 tw-bg-iron-950 tw-py-3"><div className="tw-px-4 md:tw-px-8">{t20}{t28}</div></div>;
    $[83] = t20;
    $[84] = t28;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== t19 || $[87] !== t29) {
    t30 = <div className="tw-flex tw-flex-col tw-h-full">{t19}{t29}</div>;
    $[86] = t19;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  return t30;
};

// Use React.memo to prevent unnecessary rerenders
export default React.memo(ArtworkStep);